import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Eye, Plus, Filter, ArrowLeftRight, FolderOpen, Trash2, Undo2, Upload } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTenantConfig } from '../contexts/TenantConfigContext';
import DataTable from '../components/DataTable';
import ClientModal from '../components/ClientModal';
import ClientTransferModal from '../components/ClientTransferModal';
import CreateClientModal from '../components/CreateClientModal';
import MasavUploadModal from '../components/MasavUploadModal';
import MasavSummaryModal from '../components/MasavSummaryModal';
import { masavApi, handleMasavApiError, type MasavPreviewResult } from '../services/masavService';
import IconButton from '../components/common/IconButton';
import ConfirmDialog from '../components/ConfirmDialog';
import SmartSearchBar from '../components/leads/SmartSearchBar';
import StatusFilterTabs from '../components/leads/StatusFilterTabs';
import ClientRoleIndicatorChip from '../components/clients/ClientRoleIndicatorChip';
import ClientFilterDrawer from '../components/clients/ClientFilterDrawer';
import StatusCellWithTooltip from '../components/clients/StatusCellWithTooltip';
import {
  ClientDto,
  ClientFilterDto,
  clientsApi,
  handleApiError,
  formatDate,
  BulkClientAssignmentDto
} from '../services/clientsService';
import { usersApi, UserDto } from '../services/usersService';
import {
  statusesApi,
  diseasesApi,
  StatusDto,
  DiseaseDto
} from '../services/settingsService';

const PageContainer = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
  direction: rtl;
  background: white;
  min-height: 100vh;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const UnassignedBadge = styled.span`
  background: var(--error-color);
  color: white;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;

  @media (max-width: 767px) {
    flex-direction: column;
    width: 100%;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: var(--primary-color);
          color: white;
          border: 1px solid var(--primary-color);
          &:hover {
            background: transparent;
            color: var(--primary-color);
          }
        `;
      case 'danger':
        return `
          background: var(--error-color);
          color: white;
          &:hover { background: #d32f2f; }
        `;
      default:
        return `
          background: var(--surface-color);
          color: var(--text-primary);
          border: 1px solid var(--border-color);
          &:hover { background: var(--hover-background); }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FilterContainer = styled.div`
  background: var(--surface-color);
  border-radius: var(--border-radius-lg);
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: var(--shadow-light);
`;

const FilterRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  gap: 16px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const AdvancedFilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: transparent;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: var(--hover-background);
    border-color: var(--primary-color);
    color: var(--primary-color);
  }
`;

const TabContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
`;

const TabButton = styled.button<{ $active: boolean }>`
  padding: 10px 20px;
  border: 1px solid transparent;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;

  ${props => props.$active ? `
    background: var(--primary-color);
    color: white;
    border-color: var(--primary-color);
  ` : `
    background: white;
    color: var(--text-secondary);
    border-color: var(--border-color);

    &:hover {
      border-color: var(--primary-color);
      color: var(--primary-color);
    }
  `}
`;

const BulkActions = styled.div`
  background: white;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  padding: 12px 20px;
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;

const BulkInfo = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
`;

const BulkButtons = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;

  @media (max-width: 767px) {
    flex-direction: column;
    width: 100%;
  }
`;

const BulkSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  background: white;
  min-width: 180px;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }

  @media (max-width: 767px) {
    width: 100%;
  }
`;

const BulkButton = styled.button<{ variant?: 'primary' | 'danger' }>`
  padding: 8px 14px;
  border-radius: var(--border-radius-sm);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => props.variant === 'primary' ? `
    background: var(--primary-color);
    color: white;
    border: 1px solid var(--primary-color);
    &:hover:not(:disabled) { background: transparent; color: var(--primary-color); }
  ` : `
    background: white;
    color: var(--text-primary);
    border: 1px solid var(--border-color);
    &:hover:not(:disabled) { background: var(--hover-background); }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ActionsCell = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: center;
`;

const ErrorMessage = styled.div`
  background: var(--error-light);
  color: var(--error-color);
  padding: 12px 16px;
  border-radius: var(--border-radius-md);
  margin-bottom: 20px;
  border: 1px solid var(--error-color);
`;

const ClientsPage: React.FC = () => {
  const { user, refreshSecretaryAssignments } = useAuth();
  const { config } = useTenantConfig();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [clients, setClients] = useState<ClientDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedClients, setSelectedClients] = useState<number[]>([]);
  const [statuses, setStatuses] = useState<StatusDto[]>([]);
  const [diseases, setDiseases] = useState<DiseaseDto[]>([]);
  const [caseManagers, setCaseManagers] = useState<UserDto[]>([]);
  const [caseManagerLoads, setCaseManagerLoads] = useState<Record<number, number>>({});
  const [unassignedCount, setUnassignedCount] = useState(0);

  // Check user permissions
  const isSystemAdmin = user?.permissionLevel === 1;
  const isSecretary = user?.permissionLevel === 4;
  const isCaseManagerSupervisor = user?.permissionLevel === 3 && user?.isSupervisor === true;
  const showAllClients = isSystemAdmin || isCaseManagerSupervisor;

  // Current tab - read from URL (Secretary לא רואה את הטאב "לא מוקצים")
  const [activeTab, setActiveTab] = useState<'all' | 'unassigned'>(() => {
    const tab = searchParams.get('tab');
    return tab === 'unassigned' && isSystemAdmin ? 'unassigned' : 'all';
  });

  // Pagination - read from URL
  const [currentPage, setCurrentPage] = useState(() => {
    const page = searchParams.get('page');
    return page ? parseInt(page) : 1;
  });
  const [pageSize, setPageSize] = useState(() => {
    const size = searchParams.get('pageSize');
    return size ? parseInt(size) : 25;
  });
  const [totalCount, setTotalCount] = useState(0);

  // Filters - read from URL
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState<string[]>(() => {
    const statuses = searchParams.get('statuses');
    return statuses ? statuses.split(',') : [];
  });
  const [caseManagerFilter, setCaseManagerFilter] = useState<string>(() => searchParams.get('caseManager') || '');
  const [diseaseFilter, setDiseaseFilter] = useState<string>(() => searchParams.get('disease') || '');
  const [dateFromFilter, setDateFromFilter] = useState(() => searchParams.get('dateFrom') || '');
  const [dateToFilter, setDateToFilter] = useState(() => searchParams.get('dateTo') || '');

  // Bulk actions
  const [bulkAssignCaseManager, setBulkAssignCaseManager] = useState<string>('');

  // Modals
  const [showClientModal, setShowClientModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showCreateClientModal, setShowCreateClientModal] = useState(false);
  const [showMasavModal, setShowMasavModal] = useState(false);
  const [showMasavSummary, setShowMasavSummary] = useState(false);
  const [masavFile, setMasavFile] = useState<File | null>(null);
  const [masavMonth, setMasavMonth] = useState('');
  const [masavPreview, setMasavPreview] = useState<MasavPreviewResult | null>(null);
  const [masavLoading, setMasavLoading] = useState(false);
  const [masavError, setMasavError] = useState<string | null>(null);
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [viewingClient, setViewingClient] = useState<ClientDto | null>(null);
  const [transferringClient, setTransferringClient] = useState<ClientDto | null>(null);

  // Delete / revert dialogs
  const [deletingClient, setDeletingClient] = useState<ClientDto | null>(null);
  const [revertingClient, setRevertingClient] = useState<ClientDto | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Permission to delete / revert: System Admin, Case Manager Supervisor, or assigned Case Manager.
  // Secretary cannot delete/revert clients (admin-only operations).
  const canManageClient = useCallback((client: ClientDto): boolean => {
    if (isSystemAdmin) return true;
    if (isCaseManagerSupervisor) return true;
    if (user?.permissionLevel === 3 && client.assignedCaseManagerId === user?.id) return true;
    return false;
  }, [isSystemAdmin, isCaseManagerSupervisor, user?.permissionLevel, user?.id]);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();

    if (activeTab !== 'all') params.set('tab', activeTab);
    if (currentPage > 1) params.set('page', currentPage.toString());
    if (pageSize !== 25) params.set('pageSize', pageSize.toString());
    if (searchTerm) params.set('search', searchTerm);
    if (statusFilter.length > 0) params.set('statuses', statusFilter.join(','));
    if (caseManagerFilter) params.set('caseManager', caseManagerFilter);
    if (diseaseFilter) params.set('disease', diseaseFilter);
    if (dateFromFilter) params.set('dateFrom', dateFromFilter);
    if (dateToFilter) params.set('dateTo', dateToFilter);

    setSearchParams(params, { replace: true });
  }, [activeTab, currentPage, pageSize, searchTerm, statusFilter, caseManagerFilter, diseaseFilter, dateFromFilter, dateToFilter, setSearchParams]);

  const loadClients = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let filterParams: ClientFilterDto = {
        page: currentPage,
        pageSize: pageSize,
        searchTerm: searchTerm || undefined,
        statusIds: statusFilter.length > 0 ? statusFilter.map(id => parseInt(id)) : undefined,
        diseaseIds: diseaseFilter ? [parseInt(diseaseFilter)] : undefined,
        dateFrom: dateFromFilter || undefined,
        dateTo: dateToFilter || undefined
      };

      if (activeTab === 'unassigned') {
        filterParams.unassignedOnly = true;
      } else {
        if (caseManagerFilter) {
          if (caseManagerFilter === '-1') {
            // "לא מוקצה" - filter for unassigned clients
            filterParams.unassignedOnly = true;
          } else {
            filterParams.assignedCaseManagerId = parseInt(caseManagerFilter);
          }
        } else if (!showAllClients && !isSecretary) {
          filterParams.assignedCaseManagerId = user?.id;
        }
      }

      const result = await clientsApi.getAll(filterParams);
      setClients(result.clients);
      setTotalCount(result.totalCount);

      if (activeTab === 'unassigned') {
        setUnassignedCount(result.totalCount);
      }
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm, statusFilter, caseManagerFilter, diseaseFilter, dateFromFilter, dateToFilter, activeTab, showAllClients, user?.id, isSecretary]);

  const loadUnassignedCount = useCallback(async () => {
    if (!isSystemAdmin) return;
    try {
      const result = await clientsApi.getAll({ unassignedOnly: true, page: 1, pageSize: 1 });
      setUnassignedCount(result.totalCount);
    } catch (err) {
      console.error('Error loading unassigned count:', err);
    }
  }, [isSystemAdmin]);

  const loadInitialData = useCallback(async () => {
    try {
      if (isSecretary) {
        setStatuses([]);
        setDiseases([]);
        setCaseManagers([]);
        setCaseManagerLoads([]);
        return;
      }

      const [statusesData, diseasesData, caseManagersData, loadsData] = await Promise.all([
        statusesApi.search('', 2),
        diseasesApi.getAll(),
        usersApi.getCaseManagers(),
        clientsApi.getCaseManagerLoads(),
      ]);

      setStatuses(statusesData);
      setDiseases(diseasesData);
      setCaseManagers(caseManagersData);
      setCaseManagerLoads(loadsData);
    } catch (err) {
      setError(handleApiError(err));
    }
  }, [isSecretary]);

  useEffect(() => {
    loadInitialData();
    loadUnassignedCount();
  }, [loadInitialData, loadUnassignedCount]);

  // עבור מזכירה: לרענן את רשימת ההקצאות שלה בכניסה לעמוד — לוודא טריות
  useEffect(() => {
    if (isSecretary) {
      refreshSecretaryAssignments();
    }
  }, [isSecretary, refreshSecretaryAssignments]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  const handleTabChange = (tab: 'all' | 'unassigned') => {
    setActiveTab(tab);
    setCurrentPage(1);
    setSelectedClients([]);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter([]);
    setCaseManagerFilter('');
    setDiseaseFilter('');
    setDateFromFilter('');
    setDateToFilter('');
    setCurrentPage(1);
  };

  const handleAdvancedFilter = (filters: {
    caseManagerFilter: string;
    diseaseFilter: string;
    dateFrom: string;
    dateTo: string;
  }) => {
    setCaseManagerFilter(filters.caseManagerFilter);
    setDiseaseFilter(filters.diseaseFilter);
    setDateFromFilter(filters.dateFrom);
    setDateToFilter(filters.dateTo);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number, newPageSize: number) => {
    setCurrentPage(page);
    setPageSize(newPageSize);
  };

  const handleSelectClient = (clientId: number) => {
    setSelectedClients(prev =>
      prev.includes(clientId)
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  const handleSelectAll = () => {
    if (selectedClients.length === clients.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(clients.map(client => client.id));
    }
  };

  const handleBulkAssign = async () => {
    if (!bulkAssignCaseManager || selectedClients.length === 0) return;

    try {
      const assignmentData: BulkClientAssignmentDto = {
        clientIds: selectedClients,
        caseManagerId: parseInt(bulkAssignCaseManager)
      };

      const result = await clientsApi.bulkAssignToCaseManager(assignmentData);

      if (result.success) {
        setSelectedClients([]);
        setBulkAssignCaseManager('');
        loadClients();
        loadInitialData();
        loadUnassignedCount();
      } else {
        setError(`הקצאה נכשלה: ${result.errors.join(', ')}`);
      }
    } catch (err) {
      setError(handleApiError(err));
    }
  };

  const handleViewClient = async (client: ClientDto) => {
    try {
      const fullClient = await clientsApi.getById(client.id);
      setViewingClient(fullClient);
      setShowClientModal(true);
    } catch (err) {
      setError(handleApiError(err));
    }
  };

  const handleTransferClient = (client: ClientDto) => {
    setTransferringClient(client);
    setShowTransferModal(true);
  };

  const handleManageClient = (client: ClientDto) => {
    navigate(`/clients/${client.id}/manage`);
  };

  const handleTransferComplete = () => {
    loadClients();
    loadInitialData();
    loadUnassignedCount();
  };

  const handleConfirmDelete = async () => {
    if (!deletingClient) return;
    try {
      setActionLoading(true);
      await clientsApi.deleteClient(deletingClient.id);
      setDeletingClient(null);
      loadClients();
      loadInitialData();
      loadUnassignedCount();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmRevert = async () => {
    if (!revertingClient) return;
    try {
      setActionLoading(true);
      await clientsApi.revertToLead(revertingClient.id);
      setRevertingClient(null);
      loadClients();
      loadInitialData();
      loadUnassignedCount();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setActionLoading(false);
    }
  };

  const handleClientCreated = () => {
    loadClients();
    loadInitialData();
    loadUnassignedCount();
  };

  const handleMasavContinue = async (payload: { file: File; selectedMonth: string }) => {
    setShowMasavModal(false);
    setMasavFile(payload.file);
    setMasavMonth(payload.selectedMonth);
    setMasavPreview(null);
    setMasavError(null);
    setMasavLoading(true);
    setShowMasavSummary(true);
    try {
      const result = await masavApi.preview(payload.file, payload.selectedMonth);
      setMasavPreview(result);
    } catch (err) {
      setMasavError(`שגיאה בעיבוד הקובץ: ${handleMasavApiError(err)}`);
    } finally {
      setMasavLoading(false);
    }
  };

  // Dynamic page title
  const getPageTitle = () => {
    if (isSystemAdmin) return 'ניהול לקוחות';
    if (isCaseManagerSupervisor) return 'לקוחות (מפקח)';
    return 'הלקוחות שלי';
  };

  const columns = [
    ...(isSystemAdmin && activeTab === 'unassigned' ? [{
      key: 'select',
      title: 'בחירה',
      width: '60px',
      render: (_: any, record: ClientDto) => (
        <input
          type="checkbox"
          checked={selectedClients.includes(record.id)}
          onChange={() => handleSelectClient(record.id)}
        />
      )
    }] : []),
    {
      key: 'fullName',
      title: 'שם מלא',
      width: '180px'
    },
    {
      key: 'idNumber',
      title: 'תעודת זהות',
      width: '120px'
    },
    {
      key: 'phone',
      title: 'טלפון',
      width: '130px'
    },
    ...(showAllClients ? [{
      key: 'assignedCaseManagerName',
      title: 'מנהל תיק',
      width: '140px',
      render: (value: string) => value || 'לא מוקצה'
    }] : []),
    {
      key: 'currentStatusName',
      title: 'סטטוס',
      width: '140px',
      render: (value: string, record: ClientDto) => (
        <StatusCellWithTooltip
          currentStatusName={value}
          currentStatusColor={record.currentStatusColor}
          currentStages={record.currentStages}
        />
      )
    },
    {
      key: 'primaryDiseaseName',
      title: 'מחלה עיקרית',
      width: '140px',
      render: (value: string) => value || '-'
    },
    {
      key: 'contractSignedDate',
      title: 'תאריך חתימה',
      width: '120px',
      render: (value: string) => value ? formatDate(value) : '-'
    },
    {
      key: 'daysInTreatment',
      title: 'ימים בטיפול',
      width: '100px',
      render: (value: number) => value > 0 ? `${value}` : '-'
    },
    {
      key: 'actions',
      title: 'פעולות',
      width: '220px',
      render: (_: any, record: ClientDto) => (
        <ActionsCell>
          <IconButton
            icon={Eye}
            onClick={(e) => { e.stopPropagation(); handleViewClient(record); }}
            title="צפייה בלקוח"
          />
          <IconButton
            icon={FolderOpen}
            onClick={(e) => { e.stopPropagation(); handleManageClient(record); }}
            title="ניהול תיק"
            variant="primary"
          />
          {isSystemAdmin && (
            <IconButton
              icon={ArrowLeftRight}
              onClick={(e) => { e.stopPropagation(); handleTransferClient(record); }}
              title="העברת לקוח"
            />
          )}
          {canManageClient(record) && record.originalLeadId != null && (
            <IconButton
              icon={Undo2}
              onClick={(e) => { e.stopPropagation(); setRevertingClient(record); }}
              title="החזרה לליד"
            />
          )}
          {canManageClient(record) && (
            <IconButton
              icon={Trash2}
              onClick={(e) => { e.stopPropagation(); setDeletingClient(record); }}
              title="מחיקת לקוח"
              variant="danger"
            />
          )}
        </ActionsCell>
      )
    }
  ];

  return (
    <PageContainer>
      <PageHeader>
        <HeaderRight>
          <PageTitle>
            {getPageTitle()}
            {isSystemAdmin && unassignedCount > 0 && activeTab !== 'unassigned' && (
              <UnassignedBadge>{unassignedCount} חדשים</UnassignedBadge>
            )}
          </PageTitle>
          <ClientRoleIndicatorChip
            permissionLevel={user?.permissionLevel}
            isSupervisor={user?.isSupervisor}
          />
        </HeaderRight>
        <HeaderActions>
          {config?.bankStandingOrderEnabled && (isSystemAdmin || isSecretary) && (
            <Button variant="primary" onClick={() => setShowMasavModal(true)}>
              <Upload size={16} />
              העלאת קובץ תשלומי מס"ב
            </Button>
          )}
          {(isSystemAdmin || user?.permissionLevel === 3 || isSecretary) && (
            <Button variant="primary" onClick={() => setShowCreateClientModal(true)}>
              <Plus size={16} />
              לקוח חדש
            </Button>
          )}
        </HeaderActions>
      </PageHeader>

      {error && (
        <ErrorMessage>
          {error}
        </ErrorMessage>
      )}

      {/* Tab buttons for System Admin */}
      {isSystemAdmin && (
        <TabContainer>
          <TabButton
            $active={activeTab === 'all'}
            onClick={() => handleTabChange('all')}
          >
            כל הלקוחות
          </TabButton>
          <TabButton
            $active={activeTab === 'unassigned'}
            onClick={() => handleTabChange('unassigned')}
          >
            להקצאה ({unassignedCount})
          </TabButton>
        </TabContainer>
      )}

      <FilterContainer>
        <SmartSearchBar
          value={searchTerm}
          onChange={(value) => {
            setSearchTerm(value);
            setCurrentPage(1);
          }}
          placeholder="חיפוש לפי שם, תעודת זהות או טלפון..."
        />

        <FilterRow>
          <StatusFilterTabs
            statuses={statuses}
            selectedStatuses={statusFilter}
            onChange={(values) => {
              setStatusFilter(values);
              setCurrentPage(1);
            }}
          />
          <AdvancedFilterButton onClick={() => setShowFilterDrawer(true)}>
            <Filter size={16} />
            מסננים מתקדמים
          </AdvancedFilterButton>
        </FilterRow>
      </FilterContainer>

      <ClientFilterDrawer
        isOpen={showFilterDrawer}
        onClose={() => setShowFilterDrawer(false)}
        onApply={handleAdvancedFilter}
        onClear={handleClearFilters}
        filters={{
          caseManagerFilter,
          diseaseFilter,
          dateFrom: dateFromFilter,
          dateTo: dateToFilter
        }}
        diseases={diseases}
        caseManagers={caseManagers}
        caseManagerLoads={caseManagerLoads}
        showCaseManagerFilter={showAllClients && activeTab === 'all'}
      />

      {isSystemAdmin && activeTab === 'unassigned' && selectedClients.length > 0 && (
        <BulkActions>
          <BulkInfo>
            נבחרו {selectedClients.length} לקוחות
          </BulkInfo>
          <BulkButtons>
            <BulkSelect
              value={bulkAssignCaseManager}
              onChange={(e) => setBulkAssignCaseManager(e.target.value)}
            >
              <option value="">בחר מנהל תיק</option>
              {caseManagers.map(manager => (
                <option key={manager.id} value={manager.id}>
                  {manager.fullName} ({caseManagerLoads[manager.id] || 0} תיקים)
                </option>
              ))}
            </BulkSelect>
            <BulkButton
              variant="primary"
              onClick={handleBulkAssign}
              disabled={!bulkAssignCaseManager}
            >
              הקצה
            </BulkButton>
          </BulkButtons>
        </BulkActions>
      )}

      <DataTable
        data={clients}
        columns={columns}
        loading={loading}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: totalCount,
          onChange: handlePageChange
        }}
        actions={
          isSystemAdmin && activeTab === 'unassigned' ? (
            <input
              type="checkbox"
              checked={selectedClients.length === clients.length && clients.length > 0}
              onChange={handleSelectAll}
              title="בחר הכל"
            />
          ) : undefined
        }
      />

      <ClientModal
        isOpen={showClientModal}
        onClose={() => {
          setShowClientModal(false);
          setViewingClient(null);
        }}
        client={viewingClient}
      />

      <ClientTransferModal
        isOpen={showTransferModal}
        onClose={() => {
          setShowTransferModal(false);
          setTransferringClient(null);
        }}
        client={transferringClient}
        onTransferComplete={handleTransferComplete}
      />

      <CreateClientModal
        isOpen={showCreateClientModal}
        onClose={() => setShowCreateClientModal(false)}
        onClientCreated={handleClientCreated}
      />

      <MasavUploadModal
        isOpen={showMasavModal}
        onClose={() => setShowMasavModal(false)}
        onContinue={handleMasavContinue}
      />

      <MasavSummaryModal
        isOpen={showMasavSummary}
        onClose={() => setShowMasavSummary(false)}
        file={masavFile}
        month={masavMonth}
        preview={masavPreview}
        loading={masavLoading}
        error={masavError}
        onCommitted={() => loadClients()}
      />

      <ConfirmDialog
        isOpen={deletingClient !== null}
        onClose={() => setDeletingClient(null)}
        onConfirm={handleConfirmDelete}
        title="מחיקת לקוח"
        message={
          <span>
            האם אתה בטוח שברצונך למחוק את הלקוח <strong>{deletingClient?.fullName}</strong>?
            <br /><br />
            הלקוח יוסר מרשימת הלקוחות, אך כל הנתונים (פגישות, תשלומים, מסמכים) יישמרו במערכת לצורך תיעוד.
          </span>
        }
        confirmText="מחק לקוח"
        cancelText="ביטול"
        type="danger"
        loading={actionLoading}
      />

      <ConfirmDialog
        isOpen={revertingClient !== null}
        onClose={() => setRevertingClient(null)}
        onConfirm={handleConfirmRevert}
        title="החזרה לרשימת הלידים"
        message={
          <span>
            <strong>אזהרה חמורה</strong> - פעולה זו תמחק לצמיתות את כל הנתונים של הלקוח{' '}
            <strong>{revertingClient?.fullName}</strong>:
            <br /><br />
            • כל הפגישות והתורים
            <br />
            • כל רישומי הוועדות
            <br />
            • כל התשלומים ותוכניות התשלום
            <br />
            • כל החיובים והעמלות
            <br />
            • כל המסמכים והתיקיות
            <br />
            • כל שלבי התהליך והמידע הרפואי
            <br /><br />
            החוזה החתום יבוטל והליד יוחזר לרשימת הלידים הפתוחים.
            <br /><br />
            <strong>פעולה זו אינה ניתנת לשחזור!</strong>
          </span>
        }
        confirmText="החזר לליד ומחק נתונים"
        cancelText="ביטול"
        type="danger"
        loading={actionLoading}
      />
    </PageContainer>
  );
};

export default ClientsPage;
